import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearch } from '../context/SearchContext';
import SearchPreview from './SearchPreview';

const normalizeOptionLabel = (opt) => {
  if (!opt) return '';
  if (typeof opt === 'string') return opt;
  return opt.label || opt.value || '';
};

const buildOptions = (rawSuggestions = [], searchTerm = '') => {
  const trimmed = String(searchTerm || '').trim();
  const base = trimmed ? [{ type: 'query', value: trimmed, label: `Search: “${trimmed}”` }] : [];
  const mapped = Array.isArray(rawSuggestions)
    ? rawSuggestions.map((s) => ({
        type: s.type || 'suggestion',
        value: s.value || s.label || '',
        label: s.label || s.value || '',
        meta: s.meta || null,
      }))
    : [];
  return [...base, ...mapped].filter((o) => String(o.value || o.label || '').trim());
};

export default function SmartSearch() {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const {
    searchTerm,
    setSearchTerm,
    searchSources,
    setSearchSources,
    performSearch,
    fetchSuggestions,
  } = useSearch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(() => buildOptions([], searchTerm));
  const [inputValue, setInputValue] = useState(searchTerm || '');
  const lastRequestId = useRef(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterOpen = Boolean(filterAnchorEl);

  const containerSx = useMemo(
    () => ({
      position: 'relative',
      borderRadius: 999,
      backgroundColor: alpha(theme.palette.common.white, 0.14),
      border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.18),
      },
      width: { xs: '100%', md: 420 },
    }),
    [theme]
  );

  const runSuggest = useCallback(
    async (value) => {
      const term = String(value || '').trim();
      if (!term) {
        setOptions(buildOptions([], ''));
        return;
      }
      const reqId = ++lastRequestId.current;
      setLoading(true);
      try {
        const suggestions = await fetchSuggestions(term);
        if (reqId !== lastRequestId.current) return;
        setOptions(buildOptions(suggestions, term));
      } finally {
        if (reqId === lastRequestId.current) setLoading(false);
      }
    },
    [fetchSuggestions]
  );

  useEffect(() => {
    setInputValue(searchTerm || '');
  }, [searchTerm]);

  useEffect(() => {
    const handle = setTimeout(() => void runSuggest(inputValue), 250);
    return () => clearTimeout(handle);
  }, [inputValue, runSuggest]);

  const submit = useCallback(
    async (value) => {
      const next = String(value || '').trim();
      if (!next) return;
      setSearchTerm(next);
      await performSearch(next);
      if (location.pathname !== '/enrollment-status') {
        const nextParams = new URLSearchParams();
        nextParams.set('search', next);
        navigate(`/enrollment-status?${nextParams.toString()}`);
      }
      if (isCompact) setDialogOpen(false);
    },
    [isCompact, location.pathname, navigate, performSearch, setSearchTerm]
  );

  const handleFilterToggle = (key) => (event) => {
    const next = { ...searchSources, [key]: event.target.checked };
    if (!next.dpe && !next.appsheet) return;
    setSearchSources(next);
  };

  const filterButton = (
    <IconButton
      aria-label="Search filters"
      onClick={(event) => setFilterAnchorEl(event.currentTarget)}
      size="small"
      edge="end"
      sx={{ color: 'inherit' }}
    >
      <FilterListIcon fontSize="small" />
    </IconButton>
  );

  const sharedAutocomplete = (
    <Autocomplete
      freeSolo
      disableClearable={false}
      options={options}
      filterOptions={(x) => x} // backend already scored; keep list stable
      getOptionLabel={normalizeOptionLabel}
      loading={loading}
      value={null}
      inputValue={inputValue}
      onInputChange={(_event, value) => setInputValue(value)}
      onChange={(_event, opt) => {
        if (!opt) return;
        if (typeof opt === 'string') {
          void submit(opt);
          return;
        }
        const value = opt.type === 'query' ? inputValue : opt.value || opt.label;
        void submit(value);
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <SearchPreview option={option} />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search BAC or dealer name…"
          size={isCompact ? 'medium' : 'small'}
          variant={isCompact ? 'outlined' : 'standard'}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {filterButton}
                {params.InputProps.endAdornment}
              </Box>
            ),
            ...(isCompact
              ? {}
              : {
                  disableUnderline: true,
                  sx: {
                    px: 1.5,
                    py: 0.75,
                    color: 'inherit',
                  },
                }),
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void submit(inputValue);
            }
          }}
        />
      )}
    />
  );

  if (isCompact) {
    return (
      <>
        <IconButton
          color="inherit"
          onClick={() => setDialogOpen(true)}
          aria-label="Open search"
          size="large"
        >
          <SearchIcon />
        </IconButton>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
          <DialogTitle sx={{ pr: 6 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Search
            </Typography>
            <IconButton
              aria-label="Close search"
              onClick={() => setDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {sharedAutocomplete}
          </DialogContent>
        </Dialog>
        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Search Sources
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={searchSources?.dpe}
                  onChange={handleFilterToggle('dpe')}
                  disabled={searchSources?.dpe && !searchSources?.appsheet}
                />
              }
              label="DPE"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={searchSources?.appsheet}
                  onChange={handleFilterToggle('appsheet')}
                  disabled={searchSources?.appsheet && !searchSources?.dpe}
                />
              }
              label="EnVolve"
            />
          </Box>
        </Popover>
      </>
    );
  }

  return (
    <>
      <Box sx={containerSx}>{sharedAutocomplete}</Box>
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Search Sources
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={searchSources?.dpe}
                onChange={handleFilterToggle('dpe')}
                disabled={searchSources?.dpe && !searchSources?.appsheet}
              />
            }
            label="DPE"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchSources?.appsheet}
                onChange={handleFilterToggle('appsheet')}
                disabled={searchSources?.appsheet && !searchSources?.dpe}
              />
            }
            label="EnVolve"
          />
        </Box>
      </Popover>
    </>
  );
}
